import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCapabilities } from '@/composables/useCapabilities';
import { mapApiError } from '@/utils/apiError';
import { formatAmountToPay, formatDisplayAmount } from '@/utils/money';
import { errorCodeOf, isStaleRefusal } from '@/views/vitrine/concierge/conciergeUi';

/**
 * One concierge card's call (CONCIERGE_CHAT_FIRST.md §5.2 «Действия карточек»): the buttons are blocked while it runs;
 * success and a stale-card refusal (409 by default, `isStale` for a card whose 409s also carry reasons the client must
 * read) both re-read the feed (`reread`, awaited, so the button stays blocked until the card shows its new state); any
 * other refusal is the localized reason under the button.
 */
export function useCardAction(reread: () => Promise<void> | void,
                              isStale: (e: unknown) => boolean = isStaleRefusal) {
  const { t } = useI18n();
  const { load: loadCaps } = useCapabilities();
  const running = ref(false);
  const error = ref('');

  async function run(call: () => Promise<unknown>): Promise<boolean> {
    if (running.value) return false;
    running.value = true;
    error.value = '';
    try {
      await call();
      await reread();
      return true;
    } catch (e) {
      // The method was refused: the offer changed on the server (crypto switched off) — re-read the capabilities.
      if (errorCodeOf(e) === 'CONCIERGE_METHOD_NOT_ALLOWED') void loadCaps(true);
      if (isStale(e)) await reread();
      else error.value = mapApiError(e, t, { prefix: 'vitrine.error.', field: 'error' });
      return false;
    } finally {
      running.value = false;
    }
  }

  return { running, error, run };
}

/**
 * Number formats of the cards (the display rule of `@/utils/money`): money with cents (a crypto currency up to four
 * decimals), a token amount with 2 to 4 decimals, a percent as «3%», «2,5%».
 */
export function useCardFormat() {
  const { d, locale } = useI18n();
  const num = (v: string | number, min: number, max: number): string => {
    const n = Number(v);
    return Number.isFinite(n)
      ? new Intl.NumberFormat(locale.value, { minimumFractionDigits: min, maximumFractionDigits: max }).format(n)
      : String(v);
  };
  return {
    /** `currency` omitted — fiat (exactly two decimals). */
    money: (v: string | number, currency?: string | null) => formatDisplayAmount(v, currency, locale.value),
    /** A crypto asset's amount: «5 250,00», «12,3457». */
    token: (v: string | number, asset?: string | null) => formatDisplayAmount(v, asset ?? 'USDT', locale.value),
    /**
     * An amount the client must pay, rounded UP to the shown precision (review M5): «2 129,4219» for 2129.421825 —
     * paying what is shown always covers it. Fiat or crypto by `currency`, as {@link formatDisplayAmount}.
     */
    toPay: (v: string | number, currency: string | null | undefined) => formatAmountToPay(v, currency, locale.value),
    pct: (v: string | number) => num(v, 0, 2),
    /** A deadline with its time: a crypto quote lives hours, not days. */
    dateTime: (iso: string) => d(new Date(iso), 'shortTime'),
    date: (iso: string) => d(new Date(iso), 'long'),
  };
}
